/* eslint-disable eqeqeq */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import Button from '@components/Atoms/Button';
import { useHistory, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import ShowIf from '@components/Atoms/ShowIf';
import Typography from '@components/Atoms/Typography';
import { TestAssessment } from '@learnosityassessment';
import useWebSocket from 'react-use-websocket';
import { useSelector } from 'react-redux';
import { selectors as AuthSelectors } from '@reducers/auth';
import Timer from '@components/Atoms/Timer';
import { useGetTestlets, useGetTestletSessions, useNavigateToLETestlet } from '../hooks';
import {
  useCompleteTestletSessionMutation,
  useStartTestletSessionMutation,
  useLogTestletSessionActivityMutation,
} from '../mutations';
import {
  LockStrategy,
  StudentTestletSessionStatus,
  TestletSession,
  TestletStatus,
} from '../types';
import useSessionNotification from '../hooks/sessionNotification';
import VisuallyHidden from '@components/Atoms/VisuallyHidden/VisuallyHidden';

const getMaxTime = (testletSession: TestletSession) => {
  const timeLeft =
    testletSession.original_duration +
    testletSession.time_settings.extended_duration -
    (testletSession?.time_settings?.time_elapsed ?? 0);

  if (timeLeft <= 0) {
    return 1;
  }
  return Math.round(timeLeft * 60);
};
const getStartTime = (testletSession: TestletSession) => {
  if (
    testletSession.status === StudentTestletSessionStatus.NOT_STARTED ||
    testletSession.status === StudentTestletSessionStatus.PAUSED
  ) {
    return new Date().toISOString();
  }
  return (
    testletSession.time_settings?.updated_at ||
    testletSession.resumed_at ||
    testletSession.started_at
  );
};
// Rules:
// if currentTestlet is not found, redirect to testlets list
// if testletSession is not found, redirect to testlets list
// if currentTestlet.status is locked or not started, show a message then redirect to testlets list
// if currentTestlet.status is unlocked
// if testletSession.status is not completed, render assessment
// if testletSession.status is completed, show a message then redirect to testlets list
// if testlet is locked while student is actively taking the test, allow them to continue until they submit or exit

const AssessmentContainer = () => {
  const navigateToLearnosityExam = useNavigateToLETestlet();
  const assessmentContainerRef = useRef<HTMLDivElement>(null);
  const { testletId, testSessionId, courseId, sectionId } = useParams();
  const { mutate: startTestletSession } = useStartTestletSessionMutation(testletId);
  const { mutate: completeTestletSession } = useCompleteTestletSessionMutation(testletId);
  const history = useHistory();

  const goBack = () => {
    const path = `/tests/course/${courseId}/section/${sectionId}/testsession/${testSessionId}/testlet/${testletId}`;
    history.replace(path);
  };

  const { data: testlets, isLoading: isTestletsLoading } = useGetTestlets(testSessionId, {
    refetchOnMount: 'always',
  });
  const currentTestlet = testlets?.find(
    (testlet) => testlet.id === parseInt(testletId, 10),
  );

  const { data } = useGetTestletSessions(parseInt(testletId, 10), {
    refetchOnMount: 'always',
  });

  const testletSession = data?.find(
    (session) =>
      session.exam_section == currentTestlet?.exam_section &&
      session.course == currentTestlet?.course,
  );

  const canTakeTest = useMemo(() => {
    if (!currentTestlet || !testletSession) {
      return false;
    }

    if (currentTestlet.status == TestletStatus.NOT_STARTED) {
      return false;
    }

    if (
      currentTestlet.status == TestletStatus.UNLOCKED &&
      testletSession.status != StudentTestletSessionStatus.COMPLETED
    ) {
      return true;
    }

    if (
      currentTestlet.lock_strategy == LockStrategy.USE_REMAINING_TIME &&
      [
        StudentTestletSessionStatus.IN_PROGRESS,
        StudentTestletSessionStatus.PAUSED,
      ].includes(testletSession.status)
    ) {
      return true;
    }

    return false;
  }, [currentTestlet?.status, testletSession?.status]);

  if (!canTakeTest) {
    goBack();
  }

  // WebSocket connection
  const [shouldConnect, setShouldConnect] = useState(true);
  const token = useSelector(AuthSelectors.getToken);
  const endpoint = `/ws/testletsessions/${testletSession?.id}/activities`;
  const WS_URL = testletSession?.id
    ? `${process.env.WS_PROTOCOL}://${process.env.WS_ENDPOINT}${endpoint}?token=${token}`
    : null;
  const { sendMessage, getWebSocket } = useWebSocket(
    WS_URL,
    {
      shouldReconnect: (closeEvent) =>
        // Only reconnect if the connection was not closed intentionally
        closeEvent.code !== 1000,
      share: true,
      reconnectInterval: 3000,
      onOpen: () => {
        sendMessage(JSON.stringify({ type: 'echo_message' }));
      },
    },
    shouldConnect,
  );

  const handleAssessmentComplete = useCallback(() => {
    // Close WebSocket connection with normal closure code
    if (getWebSocket()) {
      setShouldConnect(false);
    }
    completeTestletSession(undefined, {
      onSuccess: () => {
        navigateToLearnosityExam();
      },
    });
  }, [completeTestletSession, navigateToLearnosityExam, getWebSocket]);

  const handleActivityExit = useCallback(() => {
    // Close WebSocket connection with normal closure code
    if (getWebSocket()) {
      setShouldConnect(false);
    }
    navigateToLearnosityExam();
  }, [navigateToLearnosityExam, getWebSocket]);

  const handleActivityStart = useCallback(() => {
    startTestletSession();
    setShouldConnect(true);
  }, [startTestletSession]);

  useSessionNotification();

  const testletSessionId = testletSession?.id;
  const { mutate: logTestletSessionActivity } = useLogTestletSessionActivityMutation(
    testletSessionId?.toString() ?? '',
  );

  useEffect(() => {
    if (!assessmentContainerRef.current || !canTakeTest) {
      return;
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logTestletSessionActivity({ eventType: 'TESTLET_UNFOCUSED' });
      } else {
        logTestletSessionActivity({ eventType: 'TESTLET_FOCUSED' });
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [canTakeTest, logTestletSessionActivity]);

  return (
    <Grid container component="div" minWidth="150px" direction="column" width="100%">
      <h1
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        Test Assessment
      </h1>
      <Grid
        size={{ xs: 12 }}
        flexShrink={1}
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        display="flex"
        flexDirection="row"
        px={2}
      >
        <Button onClick={goBack} id="LE_Assessment_GoBackButton" dataTestId="go-back">
          Go Back
        </Button>
        <Timer
          startTime={testletSession ? new Date(getStartTime(testletSession)) : new Date()}
          duration={testletSession ? getMaxTime(testletSession) : 0}
          onTimeOut={() => {}}
        />
      </Grid>
      <Grid size={{ xs: 12 }} ref={assessmentContainerRef} flex={1} overflow="hidden">
        {/* if test session is not found, show a message */}
        <ShowIf If={!testletSession}>
          <Typography>Testlet session not found. Please try again.</Typography>
          <Button onClick={() => navigateToLearnosityExam()}>Go to Testlets</Button>
        </ShowIf>
        {/* if current testlet is not found, show a message */}
        <ShowIf If={!currentTestlet}>
          <Typography>Testlet not found. Please try again.</Typography>
          <Button onClick={() => navigateToLearnosityExam()}>Go to Testlets</Button>
        </ShowIf>

        {/* if current testlet is found, show the assessment player */}
        <ShowIf If={!!testletSession && !!currentTestlet}>
          {/* if status is locked, show test is not available */}
          <ShowIf If={currentTestlet?.status === TestletStatus.LOCKED && !canTakeTest}>
            <Typography>Test is locked. Test cannot be taken at this time.</Typography>
            <Button onClick={() => navigateToLearnosityExam()}>Go to Testlets</Button>
          </ShowIf>
          <ShowIf If={currentTestlet?.status === TestletStatus.COMPLETED && !canTakeTest}>
            <Typography>Test is completed. Test cannot be taken at this time.</Typography>
            <Button onClick={() => navigateToLearnosityExam()}>Go to Testlets</Button>
          </ShowIf>
          <ShowIf If={currentTestlet?.status === TestletStatus.NOT_STARTED}>
            <Typography>
              Test has not started yet. Test cannot be taken at this time.
            </Typography>
            <Button onClick={() => navigateToLearnosityExam()}>Go to Testlets</Button>
          </ShowIf>
          <ShowIf If={testletSession?.status === StudentTestletSessionStatus.COMPLETED}>
            <Typography>
              Test has been completed. Test cannot be taken at this time.
            </Typography>
            <Button onClick={() => navigateToLearnosityExam()}>Go to Testlets</Button>
          </ShowIf>
          {/* if status is unlocked, show assessment player */}
          <ShowIf If={!!canTakeTest}>
            <VisuallyHidden as="h2">Assessment questions</VisuallyHidden>
            <TestAssessment
              handleActivityExit={handleActivityExit}
              handleActivitySubmit={handleAssessmentComplete}
              handleActivityStart={handleActivityStart}
              maxTime={testletSession ? getMaxTime(testletSession) : 0}
              activityTemplateId={testletSession?.activity_template ?? ''}
              userId={testletSession?.user_id?.toString() ?? ''}
              sessionId={testletSession?.session_id ?? ''}
              activityId={testletSession?.activity_id ?? ''}
              title={currentTestlet?.name ?? ''}
              subtitle=""
              isBranching={currentTestlet?.is_branching ?? false}
              testletSessionId={testletSession?.id ?? 0}
            />
          </ShowIf>
        </ShowIf>
      </Grid>
    </Grid>
  );
};

export default AssessmentContainer;
